/**
 * Permission hooks for React components
 * Check user permissions for conditional rendering and UI control disabling
 * 
 * @module hooks/usePermission
 */

import { useCallback } from 'react';
import { useAuth } from '../context/authcontext';
import type { PermissionString } from '../types/permissions';

/**
 * Check if current user has a specific permission
 * 
 * Supports:
 * - Single permission: `usePermission('patientmr:phi:visit:read')`
 * - Multiple permissions (OR logic): `usePermission(['perm1', 'perm2'])`
 *
 * @param permission - Single permission string or array of permissions
 * @returns true if user has the permission (or ANY of the permissions if array)
 * 
 * @example
 * const canReadVisits = usePermission('patientmr:phi:visit:read');
 * 
 * const canEditOrCreate = usePermission([
 *   'patientmr:phi:visit:update',
 *   'patientmr:phi:visit:create'
 * ]);
 * 
 * // In JSX:
 * {canReadVisits && <VisitPanel />}
 * <button disabled={!canEditOrCreate}>Edit Visit</button>
 */
export const usePermission = (permission: PermissionString | PermissionString[]): boolean => {
  const { user } = useAuth();

  // User not authenticated or permissions not loaded
  if (!user?.permissions || user.permissions.length === 0) {
    return false;
  }

  // Convert single permission to array
  const permissionsToCheck = Array.isArray(permission) ? permission : [permission];
  
  // Cast user permissions for comparison (they come from API as strings)
  const userPermissions = user.permissions as string[];

  // Check if user has ANY of the requested permissions (OR logic)
  return permissionsToCheck.some((perm) => userPermissions?.includes(perm as string));
};

/**
 * Check if user has ANY of the specified permissions (OR logic)
 * 
 * Alias for `usePermission()` when passing an array.
 * Use this for semantic clarity when checking multiple permissions
 *
 * @param permissions - Array of permission strings to check
 * @returns true if user has at least one permission
 * 
 * @example
 * const canManageInsurance = useHasAnyPermission([
 *   'patientmr:phi:insurance:create',
 *   'patientmr:phi:insurance:update',
 *   'patientmr:phi:insurance:delete'
 * ]);
 */
export const useHasAnyPermission = (permissions: PermissionString[]): boolean => {
  const { user } = useAuth();

  if (!user?.permissions || !Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }

  // Cast user permissions for comparison (they come from API as strings)
  const userPermissions = user.permissions as string[];

  return permissions.some((perm) => userPermissions?.includes(perm as string));
};

/**
 * Check if user has ALL of the specified permissions (AND logic)
 * 
 * All permissions must be present for this to return true.
 * Use this for operations requiring multiple capabilities.
 *
 * @param permissions - Array of permission strings (all required)
 * @returns true only if user has all permissions
 * 
 * @example
 * const canCreateAndApprove = useHasAllPermissions([
 *   'patientmr:phi:authorization:create',
 *   'patientmr:phi:authorization:approve'
 * ]);
 */
export const useHasAllPermissions = (permissions: PermissionString[]): boolean => {
  const { user } = useAuth();

  if (!user?.permissions || !Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }

  // Cast user permissions for comparison (they come from API as strings)
  const userPermissions = user.permissions as string[];

  return permissions.every((perm) => userPermissions?.includes(perm as string));
};

/**
 * Memoized permission checker for advanced scenarios
 * 
 * Returns a callback function that can be used to check permissions
 * without re-rendering on every permission change.
 *
 * @returns Function to check if permission exists
 * 
 * @example
 * const hasPermission = usePermissionChecker();
 * 
 * if (hasPermission('patientmr:phi:visit:read')) {
 *   // Do something
 * }
 */
export const usePermissionChecker = () => {
  const { user } = useAuth();

  return useCallback(
    (permission: PermissionString | PermissionString[]): boolean => {
      if (!user?.permissions || user.permissions.length === 0) {
        return false;
      }

      const permissionsToCheck = Array.isArray(permission) ? permission : [permission];
      // Cast user permissions for comparison (they come from API as strings)
      const userPermissions = user.permissions as string[];
      return permissionsToCheck.some((perm) => userPermissions?.includes(perm as string));
    },
    [user?.permissions]
  );
};

/**
 * Get all user permissions (raw array)
 * 
 * Use sparingly — prefer specific permission checks for better performance.
 * This is useful for debugging or advanced filtering scenarios.
 *
 * @returns Array of all permission strings the user has
 * 
 * @example
 * const allPerms = useAllPermissions();
 * const itemActions = items.filter(item =>
 *   allPerms.includes(`patientmr:phi:${item.type}:read`)
 * );
 */
export const useAllPermissions = (): PermissionString[] => {
  const { user } = useAuth();
  return (user?.permissions as PermissionString[]) || [];
};
